import { Alert, Badge, Table } from "react-bootstrap";
import type {
  BlockingPositionFlag,
  OvershootResidualWarningData,
} from "../lib/explainTypes";
import { formatCurrency } from "../../../lib/numbers";

interface Props {
  warning: OvershootResidualWarningData;
  resolveAccountName?: (accountId: number) => string;
}

const num = (s: string | undefined): number =>
  s == null || s === "" ? 0 : Number(s);

// Constraint flags carried by a blocking position — badge label and
// advisor remediation text. Source: s-soft-completion-019e31e8 §4.
// `lot_selection_pinned` is internal-triage only (not advisor-ready)
// and is no longer emitted by the soft-completion pass after follow-up
// #1, but is mapped here so an older payload still renders.
const FLAG_INFO: Record<
  BlockingPositionFlag,
  { label: string; variant: string; remediation: string }
> = {
  hit_gains_budget: {
    label: "Gains budget",
    variant: "warning",
    remediation:
      "Selling more breaches the yearly capital-gains budget. Raise the budget, or accept the overshoot.",
  },
  hit_wash_sale: {
    label: "Wash sale",
    variant: "warning",
    remediation:
      "The security was bought within the last 30 days. Wait 30 days, then re-rebalance.",
  },
  hit_hold_only: {
    label: "Hold only",
    variant: "info",
    remediation:
      "The client asked never to sell this position. Discuss lifting the restriction.",
  },
  lot_selection_pinned: {
    label: "Lot selection pinned",
    variant: "secondary",
    remediation:
      "Held above target with no specific cause attributed — residual fallback only.",
  },
};

// Canonical flag order so badges and remediation guidance read
// consistently regardless of payload ordering.
const FLAG_ORDER: BlockingPositionFlag[] = [
  "hit_gains_budget",
  "hit_wash_sale",
  "hit_hold_only",
  "lot_selection_pinned",
];

// One card per asset class that still overshoots its household target
// after redistribution: names the blocking positions and maps each
// constraint flag to advisor remediation guidance.
export default function RedistributionWarningCard({
  warning,
  resolveAccountName,
}: Props) {
  const accountName = (id: number): string =>
    resolveAccountName?.(id) ?? `Account ${id}`;

  const presentFlags = FLAG_ORDER.filter((flag) =>
    warning.blocking_positions.some((p) => p.flags.includes(flag)),
  );

  return (
    <Alert variant="warning" className="mb-3">
      <Alert.Heading as="h6">{warning.ac} — residual overshoot</Alert.Heading>
      <p className="mb-2">
        {formatCurrency(num(warning.residual_dollars))} still above the{" "}
        {formatCurrency(num(warning.hh_target_dollars))} household target.
      </p>

      <Table size="sm" responsive className="mb-2 bg-transparent">
        <thead>
          <tr>
            <th>Blocking position</th>
            <th className="text-end">Amount</th>
            <th>Constraints</th>
          </tr>
        </thead>
        <tbody>
          {warning.blocking_positions.map((p, i) => (
            <tr key={`${p.account_id}-${i}`}>
              <td>{accountName(p.account_id)}</td>
              <td className="text-end">{formatCurrency(num(p.dollars))}</td>
              <td>
                {p.flags.length === 0 ? (
                  <span className="text-muted">—</span>
                ) : (
                  p.flags.map((flag) => (
                    <Badge
                      key={flag}
                      bg={FLAG_INFO[flag].variant}
                      className="me-1"
                    >
                      {FLAG_INFO[flag].label}
                    </Badge>
                  ))
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {presentFlags.length > 0 && (
        <>
          <strong>How to resolve</strong>
          <ul className="mb-0">
            {presentFlags.map((flag) => (
              <li key={flag}>
                <Badge bg={FLAG_INFO[flag].variant} className="me-1">
                  {FLAG_INFO[flag].label}
                </Badge>
                {FLAG_INFO[flag].remediation}
              </li>
            ))}
          </ul>
        </>
      )}
    </Alert>
  );
}
